import type { ICaseListItem } from '@escloud/types';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { ConfirmationDialog } from '@/components/confirmation-dialog';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeleteCase } from '@/hooks';
import { ApiError } from '@/services/api-client';

interface CaseRowActionsProps {
    caseItem: ICaseListItem;
}

export function CaseRowActions({ caseItem }: CaseRowActionsProps) {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const deleteCase = useDeleteCase();

    const handleDelete = async () => {
        try {
            await deleteCase.mutateAsync(caseItem.id);
            toast.success(`Case "${caseItem.name}" moved to trash`);
            setDeleteDialogOpen(false);
        } catch (error) {
            if (error instanceof ApiError) {
                switch (error.errorCode) {
                    case 'job_active':
                        toast.error('Cannot delete case while a job is in progress');
                        break;
                    case 'not_authorized':
                        toast.error('You do not have permission to delete this case');
                        break;
                    default:
                        toast.error('Failed to delete case');
                }
            } else {
                toast.error('Failed to delete case');
            }
        }
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Open menu</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    <DropdownMenuItem className="text-destructive" onClick={() => setDeleteDialogOpen(true)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <ConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                title="Delete case"
                description={`Are you sure you want to delete "${caseItem.name}"? This case will be moved to trash and can be restored within 30 days.`}
                confirmLabel="Delete"
                cancelLabel="Cancel"
                variant="destructive"
                onConfirm={handleDelete}
                isPending={deleteCase.isPending}
            />
        </>
    );
}
